/**
 * Copyright (C) 2019 Viasat, Inc.
 * All rights reserved.
 * The information in this software is subject to change without notice and should not be construed as a commitment by Viasat, Inc.
 *
 * Viasat Proprietary
 * The Proprietary Information provided herein is proprietary to Viasat and
 * must be protected from further distribution and use. Disclosure to others,
 * use or copying without express written authorization of Viasat, is strictly prohibited.
 */

import { ThemeInterface } from '../../common/theme/AppThemeInterface';
import appTheme from '../../common/theme/appTheme.json';

const theme: ThemeInterface = appTheme;

const ICON_WIDTH = theme.hubs.iconWidth;
const ICON_HEIGHT = theme.hubs.iconHeight;
const ICON_VIEWBOX_LEFT = 0;
const ICON_VIEWBOX_TOP = 0;
const ICON_VIEWBOX_WIDTH = ICON_WIDTH;
const ICON_VIEWBOX_HEIGHT = ICON_HEIGHT;

export interface SvgElemProps {
  fill: string;
  visible: boolean;
  opacity?: number;
}

export interface HubsSvgProps {
  background: SvgElemProps;
  outerDiamond: SvgElemProps;
  innerDiamond: SvgElemProps;
  satDish: SvgElemProps;
  // weatherAlerts: SvgElemProps; // TODO: enable when weather alerts are available
  statusOff: SvgElemProps;
  statusStandby: SvgElemProps;
  statusNormal: SvgElemProps;
  statusCaution: SvgElemProps;
  statusSerious: SvgElemProps;
  statusCritical: SvgElemProps;
}

export const defaultHubsSvgProps: HubsSvgProps = {
  background: { fill: '', visible: false },
  outerDiamond: { fill: theme.hubs.outerDiamondFill, visible: true },
  innerDiamond: { fill: theme.hubs.innerDiamondFill, visible: true },
  satDish: { fill: theme.hubs.satDishFill, visible: true },
  // TODO: enable when weather alerts are available
  // weatherAlerts: {
  //   fill: theme.hubs.weatherAlertsFill,
  //   secondaryFill: theme.hubs.weatherAlertsSecondaryFill,
  //   visible: false,
  // },
  statusOff: { fill: theme.hubs.statusOffFill, visible: false },
  statusStandby: { fill: theme.hubs.statusStandbyFill, visible: false },
  statusNormal: { fill: theme.hubs.statusNormalFill, visible: false },
  statusCaution: { fill: theme.hubs.statusCautionFill, visible: false },
  statusSerious: { fill: theme.hubs.statusSeriousFill, visible: false },
  statusCritical: { fill: theme.hubs.statusCriticalFill, visible: false },
};

export const HubsMarkerIcon = (props?: HubsSvgProps): string => {
  let localProps = defaultHubsSvgProps;

  if (props) {
    localProps = props;
  }

  return `
    <svg
      version="1.1"
      xmlns="http://www.w3.org/2000/svg"
      width="${ICON_WIDTH}"
      height="${ICON_HEIGHT}"
      viewBox="${ICON_VIEWBOX_LEFT} ${ICON_VIEWBOX_TOP} ${ICON_VIEWBOX_WIDTH} ${ICON_VIEWBOX_HEIGHT}"
    >

      <rect
        id="background"
        fill="${localProps.background.fill}"
        visibility="${localProps.background.visible ? 'visible' : 'hidden'}"
        opacity="${localProps.background.opacity}"
        x="${ICON_VIEWBOX_LEFT}"
        y="${ICON_VIEWBOX_TOP}"
        width="${ICON_VIEWBOX_WIDTH}"
        height="${ICON_VIEWBOX_HEIGHT}"
      />

      <g id="diamond" transform="translate(15.000000, 15.000000) rotate(45.000000) translate(-15.000000, -15.000000) translate(5.000000, 5.000000)">
        <path
          id="outer_diamond"
          fill="${localProps.outerDiamond.fill}"
          visibility="${localProps.outerDiamond.visible ? 'visible' : 'hidden'}"
          d="M0,0 L20,0 L20,20 L0,20 L0,0 Z M2,2 L2,18 L18,18 L18,2 L2,2 Z"
        />
        
        <rect
          id="inner_diamond"
          fill="${localProps.innerDiamond.fill}"
          visibility="${localProps.innerDiamond.visible ? 'visible' : 'hidden'}"
          x="1"
          y="1"
          width="18"
          height="18"
        />
        <g 
          width="15" 
          height="15" 
          stroke="none" 
          stroke-width="1" 
          fill="none" 
          fill-rule="evenodd" 
          transform="translate(10.192388, 9.192388) rotate(-45.000000) translate(-10.9, -8.82388) translate(3.692388, 2.692388)"
          id="sat_dish"
          
          visibility="${localProps.satDish.visible ? 'visible' : 'hidden'}"
        >
          <svg width="14" height="14" viewBox="0 0 15 20">
            <g id="hub_custom-24px" transform="translate(-5.000000, -2.000000)">
              <polygon id="Path" points="0 0 24 0 24 24 0 24"></polygon>
              <path
                d="M16,6 L16,10.426 L11.574,6 L16,6 Z M12,16.813 C14.8,17.392 17.827,16.599 20,14.426 L18,12.426 L18,6 L18,5 C18,4.448 17.552,4 17,4 L16,4 L9.574,4 L7.574,2 C4.142,5.432 4.142,10.995 7.574,14.426 C8.299,15.152 9.124,15.715 10,16.134 L10,20 L6,20 L6,22 L16,22 L16,20 L12,20 L12,16.813 Z" fill="${localProps.satDish.fill}"></path>
            </g>
          </svg>
        </g>
      </g>

      <g
        id="status_icons"
      >
        <circle
          id="off"
          fill="${localProps.statusOff.fill}"
          visibility="${localProps.statusOff.visible ? 'visible' : 'hidden'}"
          cx="6.6"
          cy="6.3"
          r="2.4"
        />
        
        <path
          id="standby"
          fill="${localProps.statusStandby.fill}"
          visibility="${localProps.statusStandby.visible ? 'visible' : 'hidden'}"
          d="M6.6,1.6C4,1.6,1.9,3.7,1.9,6.3s2.1,4.7,4.7,4.7s4.7-2.1,4.7-4.7S9.2,1.6,6.6,1.6z M6.6,8.7
            c-1.3,0-2.4-1.1-2.4-2.4S5.3,4,6.6,4s2.4,1.1,2.4,2.4S7.9,8.7,6.6,8.7z"/>
        
        <circle
          id="normal"
          fill="${localProps.statusNormal.fill}"
          visibility="${localProps.statusNormal.visible ? 'visible' : 'hidden'}"
          cx="6.6"
          cy="6.3"
          r="4.7"
        />

        <rect
          id="caution"
          fill="${localProps.statusCaution.fill}"
          visibility="${localProps.statusCaution.visible ? 'visible' : 'hidden'}"
          x="1.9"
          y="3.4"
          width="9.4"
          height="5.9"
        />

        <rect
          id="serious"
          fill="${localProps.statusSerious.fill}"
          visibility="${localProps.statusSerious.visible ? 'visible' : 'hidden'}"
          x="3"
          y="2.8"
          width="7.1"
          height="7.1"
          transform="matrix(0.7071 -0.7071 0.7071 0.7071 -2.569 6.5068)"
        />
        
        <polygon
          id="critical"
          fill="${localProps.statusCritical.fill}"
          visibility="${localProps.statusCritical.visible ? 'visible' : 'hidden'}"
          points="6.6,10.4 11.3,2.3 1.9,2.3"
        />
      </g>

    </svg>
  `;
};
